import {AssigneeRepositoryPicker, DefaultAssigneePickerAnchor} from '@github-ui/item-picker/AssigneePicker'

import {AlertIcon, TableIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import {Suspense} from 'react'
import {MetadataFooterLoading} from './../metadata/MetadataFooterLoading'
import {useIssueCreateDataContext} from './../contexts/IssueCreateDataContext'
import {useIssueCreateConfigContext} from './../contexts/IssueCreateConfigContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {DefaultLabelAnchor, LabelPicker} from '@github-ui/item-picker/LabelPicker'
import {DefaultMilestonePickerAnchor, MilestonePicker} from '@github-ui/item-picker/MilestonePicker'
import {DefaultProjectPickerAnchor, ProjectPicker} from '@github-ui/item-picker/ProjectPicker'
import {SharedPicker} from '@github-ui/item-picker/SharedPicker'
import type {SharedMetadataProps} from './MetadataSelectors'

export const MetadataFooter = ({
  repo,
  owner,
  setMilestoneCallback,
  canAssign,
  canLabel,
  canMilestone,
  canProject,
}: SharedMetadataProps) => {
  const {optionConfig} = useIssueCreateConfigContext()
  const {repository, labels, setLabels, assignees, setAssignees, projects, setProjects, milestone} =
    useIssueCreateDataContext()

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
    shortcutEnabled: optionConfig.singleKeyShortcutsEnabled,
  }

  return (
    <Suspense fallback={<MetadataFooterLoading />}>
      <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap'}}>
        {repository && (canAssign || assignees.length > 0) && (
          <AssigneeRepositoryPicker
            repo={repository.name}
            owner={repository.owner.login}
            readonly={!canAssign}
            assignees={assignees}
            assigneeTokens={[]}
            anchorElement={(anchorProps, ref) => (
              <DefaultAssigneePickerAnchor
                assignees={assignees}
                readonly={!canAssign}
                anchorProps={anchorProps}
                ref={ref}
                {...sharedConfigProps}
              />
            )}
            onSelectionChange={setAssignees}
            {...sharedConfigProps}
          />
        )}
        {(canLabel || labels.length > 0) && (
          <LabelPicker
            repo={repo}
            owner={owner}
            readonly={!canLabel}
            labels={labels}
            anchorElement={(anchorProps, ref) => (
              <DefaultLabelAnchor
                readonly={!canLabel}
                labels={labels}
                anchorProps={anchorProps}
                ref={ref}
                {...sharedConfigProps}
              />
            )}
            onSelectionChanged={setLabels}
            {...sharedConfigProps}
          />
        )}
        {repository && !optionConfig.scopedProjectTitle && (canProject || projects.length > 0) && (
          <ErrorBoundary
            fallback={<SharedPicker anchorText={'Projects are unavailable'} leadingIcon={AlertIcon} readonly />}
          >
            <ProjectPicker
              pickerId="create-issue-projects-picker"
              readonly={!canProject}
              onSave={setProjects}
              selectedProjects={projects}
              firstSelectedProjectTitle={projects.length > 0 ? projects[0]!.title : ''}
              owner={repository.owner.login}
              repo={repository.name}
              includeClassicProjects={true}
              anchorElement={anchorProps => (
                <DefaultProjectPickerAnchor
                  nested={false}
                  readonly={!canMilestone}
                  anchorProps={anchorProps}
                  {...sharedConfigProps}
                />
              )}
              {...sharedConfigProps}
            />
          </ErrorBoundary>
        )}
        {/* Rendered when created from a Memex project */}
        {optionConfig.scopedProjectTitle && (
          <SharedPicker anchorText={optionConfig.scopedProjectTitle} leadingIcon={TableIcon} readonly />
        )}
        {(canMilestone || milestone !== null) && (
          <MilestonePicker
            repo={repo}
            owner={owner}
            readonly={!canMilestone}
            activeMilestone={milestone}
            anchorElement={(anchorProps, ref) => (
              <DefaultMilestonePickerAnchor
                nested={false}
                readonly={!canMilestone}
                activeMilestone={milestone}
                anchorProps={anchorProps}
                ref={ref}
                {...sharedConfigProps}
              />
            )}
            onSelectionChanged={setMilestoneCallback}
            {...sharedConfigProps}
          />
        )}
      </Box>
    </Suspense>
  )
}

try{ MetadataFooter.displayName ||= 'MetadataFooter' } catch {}